import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiModel, BulkUploadDraftSellerModel, BulkUploadDraftSellerModelConverter } from '@steelbuy/ts-shared';
import axios from '../../axios';

export const DRAFT_BULK_UPLOAD_KEY = 'draft-bulk-upload';
export const DRAFT_BULK_UPLOAD_VALIDATION_KEY = 'draft-bulk-upload-validation';

type CreateDraftBulkUploadRequest = {
    file: File;
};

export type DraftsValidationSummaryItem = {
    id: string;
    fieldChanges: {
        field: string;
        originalValue: string;
    }[];
};

export type BulkUploadDraftSellerModelWithValidation = BulkUploadDraftSellerModel & {
    draftsValidationSummary?: DraftsValidationSummaryItem[];
};

const createDraftBulkUpload = async (
    request: CreateDraftBulkUploadRequest
): Promise<BulkUploadDraftSellerModelWithValidation> => {
    const formData = new FormData();
    Object.entries(request).forEach(([field, value]) =>
        formData.append(field, typeof value === 'boolean' ? `${value}` : value)
    );
    const { data } = await axios.post<BulkUploadDraftSellerModelWithValidation>(
        `/api/universal/bulk-listing-drafts/upload`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );
    const modelConverter = new BulkUploadDraftSellerModelConverter();
    const convertedData = modelConverter.toViewModel(data as unknown as ApiModel<BulkUploadDraftSellerModel>);
    return convertedData as BulkUploadDraftSellerModelWithValidation;
};

export const useCreateBulkUpload = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: CreateDraftBulkUploadRequest) => createDraftBulkUpload(request),
        onSuccess: (data: BulkUploadDraftSellerModelWithValidation) => {
            const { draftsValidationSummary, ...draftData } = data;
            queryClient.setQueryData([DRAFT_BULK_UPLOAD_KEY, data.bulkId], draftData);
            queryClient.setQueryData([DRAFT_BULK_UPLOAD_VALIDATION_KEY, data.bulkId], draftsValidationSummary ?? []);
        },
    });
};
